import QueueAnim from 'rc-queue-anim';
import React, { FC, useCallback, useEffect, useState } from 'react';
import {
  CommentSocketEvent,
  Discussion as DiscussionType,
} from 'api/document/types/document';
import { Action, EventType, ObjectType, SocketData } from 'api/pushes';
import { useProjectWSUpdates } from 'hooks/use-project-ws-updates';
import { CommentForm } from '../comment-form';
import { Discussion } from './discussion';
import {
  DiscussionProvider,
  getDiscussionsOnStore,
  useDiscussions,
  wsAddACommentOnStore,
  wsDeleteACommentOnStore,
} from './discussions-store';
import styles from './doc-discussions.module.css';
import { DocDiscussionsEmptyState } from './empty-state';
import { DocDiscussionProps, DocDiscussionsProps } from './types';

const DocumentDiscussionsBase: FC<DocDiscussionProps> = ({
  showForm,
  editor,
  docId,
  projectId,
  commentThreadId,
  newCommentBaseContent,
  editorCommentMap = {},
  onActiveDiscussionLengthChange,
  onCloseForm,
  onDiscussionClick,
}: DocDiscussionProps) => {
  const [state, dispatch] = useDiscussions();
  const { discussions } = state;

  const [activeDiscussionIndex, setActiveDiscussion] = useState(-1);

  const makeActiveCommand = useCallback(
    (index: number, discussion: DiscussionType) => {
      if (!discussion) {
        return;
      }
      setActiveDiscussion(index);
      const commentId = `rl-comment-${discussion.commentThread.threadId}`;
      const options = {
        commentId,
      };

      editor?.execute('activeCommentCommand', options);
    },
    [editor],
  );

  // Update store with discussions from API.
  useEffect(() => {
    const getDiscussion = async () => {
      getDiscussionsOnStore(dispatch, { projectId, docId });
    };

    getDiscussion();
  }, [docId, projectId, dispatch]);

  useEffect(() => {
    const threadId = Number(commentThreadId?.split('-')[2]);
    const activeThreadIndex = discussions.findIndex(({ commentThread }) => {
      return commentThread.threadId === threadId;
    });

    if (activeThreadIndex > -1) {
      makeActiveCommand(activeThreadIndex, discussions[activeThreadIndex]);
    }
    const documentDiscussionThread = document.getElementById(
      'documentDiscussionThread',
    );
    const threadEl = documentDiscussionThread?.querySelector(
      `#commentThread-${commentThreadId}`,
    );

    threadEl?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [commentThreadId, discussions, makeActiveCommand]);

  const handleSocketUpdate = useCallback(
    (socketData: SocketData<CommentSocketEvent>) => {
      if (socketData?.data.commentContext?.id === docId) {
        const { data } = socketData;

        wsAddACommentOnStore(dispatch, data);
      }
    },
    [dispatch, docId],
  );

  const handleSocketDelete = useCallback(
    (socketData: SocketData<CommentSocketEvent>) => {
      if (socketData?.data.commentContext?.id === docId) {
        const { data } = socketData;

        wsDeleteACommentOnStore(dispatch, data);
      }
    },
    [dispatch, docId],
  );

  useProjectWSUpdates(
    projectId,
    handleSocketUpdate,
    EventType(Action.Create, ObjectType.DocumentComment),
  );
  useProjectWSUpdates(
    projectId,
    handleSocketUpdate,
    EventType(Action.Update, ObjectType.DocumentComment),
  );
  useProjectWSUpdates(
    projectId,
    handleSocketDelete,
    EventType(Action.Delete, ObjectType.DocumentComment),
  );

  const activeDiscussions = discussions.filter(
    (discussion) =>
      editorCommentMap[discussion.commentThread.threadId]?.isActive,
  );

  useEffect(() => {
    onActiveDiscussionLengthChange(activeDiscussions.length);
  }, [activeDiscussions, onActiveDiscussionLengthChange]);

  return (
    <div className={styles.discussions} id="documentDiscussionThread">
      <QueueAnim type="bottom">
        <CommentForm
          key="comment-form"
          docContent={newCommentBaseContent}
          show={showForm}
          onClose={() => {
            onCloseForm();
          }}
          editor={editor}
          documentData={{ projectId, docId }}
        />
        {activeDiscussions.length > 0 || showForm ? (
          activeDiscussions.map((discussion, disc_index) => (
            <Discussion
              editor={editor}
              editorCommentMap={editorCommentMap}
              discussion={discussion}
              isActiveDiscussion={disc_index === activeDiscussionIndex}
              key={`${discussion.commentThread.threadId}`}
              onDiscussionClick={() => {
                onDiscussionClick(discussion.commentThread.threadId.toString());
                makeActiveCommand(disc_index, discussion);
              }}
              projectId={projectId}
              docId={docId}
            />
          ))
        ) : (
          <DocDiscussionsEmptyState />
        )}
      </QueueAnim>
    </div>
  );
};

// Spreading props on the Provider which is fine as I'm adding types to actual component
/* eslint-disable react/jsx-props-no-spreading */
export const DocumentDiscussions: FC<DocDiscussionsProps> = (props) => {
  return (
    <DiscussionProvider>
      <DocumentDiscussionsBase {...props} />
    </DiscussionProvider>
  );
};
/* eslint-enable react/jsx-props-no-spreading */
